<script setup>
import { toRefs, computed } from "vue";
import { useRouter } from "vue-router";
import { formatDateTimeJaWithWeekday } from "@/utils/date.js";

const props = defineProps({
  productsData: Array,
});

const { productsData } = toRefs(props);

// orderフィールドでソートするヘルパー関数
const sortByOrder = (a, b) => {
  // orderフィールドを数値に変換（null/undefined/空文字の場合は9999）
  const orderA =
    a.order !== undefined && a.order !== null && a.order !== ""
      ? Number(a.order)
      : 9999;
  const orderB =
    b.order !== undefined && b.order !== null && b.order !== ""
      ? Number(b.order)
      : 9999;

  // NaNの場合は9999として扱う
  const numA = isNaN(orderA) ? 9999 : orderA;
  const numB = isNaN(orderB) ? 9999 : orderB;

  return numA - numB;
};

const onAirProductsData = computed(() => {
  return productsData.value
    .filter((p) => {
      if (!p.eventdate || !p.eventdate.start || !p.eventdate.end) return false;
      const now = Date.now();
      return p.eventdate.start.toDate() < now && now < p.eventdate.end.toDate();
    })
    .sort(sortByOrder);
});

const endproductsData = computed(() => {
  return productsData.value
    .filter((p) => {
      if (!p.eventdate || !p.eventdate.end) return false;
      return p.eventdate.end.toDate() < Date.now();
    })
    .sort(sortByOrder);
});

const scheduledProductsData = computed(() => {
  return productsData.value
    .filter((p) => {
      if (!p.eventdate || !p.eventdate.start) return false;
      return Date.now() < p.eventdate.start.toDate();
    })
    .sort(sortByOrder);
});

const router = useRouter();

const toItemlist = (item) => {
  // 配信クラファンの場合
  if (item.projectid) {
    router.push(`/crowdfunding/${item.projectid}`);
  } else {
    // 通常のイベントの場合
    router.push(`itemlist_v/${item.event_id}`);
  }
};

// ラベルテキストを取得
const getLabelText = (item) => {
  return item.projectid ? "配信クラファン" : "リアルタイムくじ引き";
};

// ラベルの背景色を取得
const getLabelColor = (item) => {
  return item.projectid ? "#DA3990" : "#9c27b0";
};

// タイトルを取得
const getTitle = (item) => {
  return item.projectTitle || item.eventtitle;
};

// サブタイトルを取得
const getSubtitle = (item) => {
  return item.vendor_ja || item.livenumber;
};

// 金額をフォーマット
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("ja-JP").format(amount || 0);
};

// 進捗率を計算（100%を超えても表示可能）
const getProgressPercentage = (item) => {
  if (!item.targetAmount || item.targetAmount === 0) return 0;
  const percentage = ((item.currentAmount || 0) / item.targetAmount) * 100;
  return Math.round(percentage);
};
</script>

<template>
  <div>
    <div>
      <!-- 開催中 -->
      <v-row class="pc-ma-2 cancelrow" justify="center">
        <v-col
          v-for="(productsData, index) in onAirProductsData"
          :key="index"
          cols="12"
          sm="6"
          md="4"
          class="cancelpadding"
        >
          <!-- Image with label -->
          <div class="image-container">
            <v-img
              class="clickable-area"
              :src="productsData.vendor_image"
              @click="toItemlist(productsData)"
            />
            <div class="realtime-label" :style="{ backgroundColor: getLabelColor(productsData) }">
              {{ getLabelText(productsData) }}
            </div>
          </div>

          <!-- Card content -->
          <v-card class="custom-card">
            <div class="livecontainer pa-2 clickable-area" @click="toItemlist(productsData)">
              <div class="livestatus-onair mb-2">
                <p>開催中</p>
              </div>
              <h3 class="title mb-2">{{ getTitle(productsData) }}</h3>
              <p class="title mb-2">{{ getSubtitle(productsData) }}</p>

              <!-- 配信クラファンの進捗情報 -->
              <div v-if="productsData.projectid" class="crowdfunding-progress mb-2">
                <div class="progress-amount">
                  <span class="progress-label">現在の応援総額</span>
                  <span class="progress-value"
                    >{{ formatCurrency(productsData.currentAmount) }}円</span
                  >
                </div>
                <div class="progress-bar-wrapper">
                  <div
                    class="progress-bar-fill"
                    :style="{
                      width: Math.min(getProgressPercentage(productsData), 100) + '%',
                    }"
                  >
                    <span class="progress-percentage"
                      >{{ getProgressPercentage(productsData) }}%</span
                    >
                  </div>
                </div>
                <div class="target-amount">
                  <span class="target-label">目標金額</span>
                  <span class="target-value"
                    >{{ formatCurrency(productsData.targetAmount) }}円</span
                  >
                </div>
              </div>

              <p class="time mb-2">
                <v-icon size="20">mdi-timer</v-icon>
                {{ formatDateTimeJaWithWeekday(productsData.eventdate.end.toDate()) }}まで
              </p>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- 開催予定 -->
      <v-row class="pc-ma-2 cancelrow" justify="center">
        <v-col
          v-for="(productsData, index) in scheduledProductsData"
          :key="index"
          cols="12"
          sm="6"
          md="4"
          class="cancelpadding"
        >
          <!-- Image with label -->
          <div class="image-container">
            <v-img
              class="clickable-area"
              :src="productsData.vendor_image"
              @click="toItemlist(productsData)"
            />
            <div class="realtime-label" :style="{ backgroundColor: getLabelColor(productsData) }">
              {{ getLabelText(productsData) }}
            </div>
          </div>

          <!-- Card content -->
          <v-card class="custom-card">
            <div class="livecontainer pa-2 clickable-area" @click="toItemlist(productsData)">
              <div class="livestatus-scheduled mb-2">
                <p>開催予定</p>
              </div>
              <h3 class="title mb-2">{{ getTitle(productsData) }}</h3>
              <p class="title mb-2">{{ getSubtitle(productsData) }}</p>

              <!-- 配信クラファンの進捗情報 -->
              <div v-if="productsData.projectid" class="crowdfunding-progress mb-2">
                <div class="progress-amount">
                  <span class="progress-label">現在の応援総額</span>
                  <span class="progress-value"
                    >{{ formatCurrency(productsData.currentAmount) }}円</span
                  >
                </div>
                <div class="progress-bar-wrapper">
                  <div
                    class="progress-bar-fill"
                    :style="{
                      width: Math.min(getProgressPercentage(productsData), 100) + '%',
                    }"
                  >
                    <span class="progress-percentage"
                      >{{ getProgressPercentage(productsData) }}%</span
                    >
                  </div>
                </div>
                <div class="target-amount">
                  <span class="target-label">目標金額</span>
                  <span class="target-value"
                    >{{ formatCurrency(productsData.targetAmount) }}円</span
                  >
                </div>
              </div>

              <p class="time mb-2">
                <v-icon size="20">mdi-timer</v-icon>
                {{ formatDateTimeJaWithWeekday(productsData.eventdate.start.toDate()) }}から
              </p>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <!-- 開催終了 -->
      <v-row class="pc-ma-2 cancelrow" justify="center">
        <v-col
          v-for="(productsData, index) in endproductsData"
          :key="index"
          cols="12"
          sm="6"
          md="4"
          class="cancelpadding"
        >
          <!-- Image with label -->
          <div class="image-container">
            <v-img
              class="clickable-area"
              :src="productsData.vendor_image"
              @click="toItemlist(productsData)"
            />
            <div class="realtime-label" :style="{ backgroundColor: getLabelColor(productsData) }">
              {{ getLabelText(productsData) }}
            </div>
          </div>

          <!-- Card content -->
          <v-card class="custom-card">
            <div class="livecontainer pa-2 clickable-area" @click="toItemlist(productsData)">
              <div class="livestatus-end mb-2">
                <p>開催終了</p>
              </div>
              <h3 class="title mb-2">{{ getTitle(productsData) }}</h3>
              <p class="title mb-2">{{ getSubtitle(productsData) }}</p>

              <!-- 配信クラファンの進捗情報 -->
              <div v-if="productsData.projectid" class="crowdfunding-progress mb-2">
                <div class="progress-amount">
                  <span class="progress-label">現在の応援総額</span>
                  <span class="progress-value"
                    >{{ formatCurrency(productsData.currentAmount) }}円</span
                  >
                </div>
                <div class="progress-bar-wrapper">
                  <div
                    class="progress-bar-fill"
                    :style="{
                      width: Math.min(getProgressPercentage(productsData), 100) + '%',
                    }"
                  >
                    <span class="progress-percentage"
                      >{{ getProgressPercentage(productsData) }}%</span
                    >
                  </div>
                </div>
                <div class="target-amount">
                  <span class="target-label">目標金額</span>
                  <span class="target-value"
                    >{{ formatCurrency(productsData.targetAmount) }}円</span
                  >
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<style scoped>
.iconcontainer {
  margin: 30px 0 0 0;
  display: flex;
  /* align-items: center; */
  justify-content: center;
}
.icon {
  width: 50px;
  height: 50px;
  margin-right: 5px;
  border-radius: 100px;
  overflow: hidden;
}
.icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.livecontainer {
  border: solid 1px #454545;
}
.livestatus-onair {
  border: solid 1px rgb(178, 123, 211);
  display: inline-block;
  float: left;
  margin: 15px 0 5px 20px;
  background-color: rgb(178, 123, 211);
  color: white;
  padding: 5px 5px 5px 5px;
  font-size: 12px;
  /* text-align: left; */
}
.livestatus-scheduled {
  border: solid 1px #ffcceb;
  display: inline-block;
  float: left;
  margin: 15px 0 5px 20px;
  background-color: #ffcceb;
  color: white;
  padding: 5px 5px 5px 5px;
  font-size: 12px;
  /* text-align: left; */
}
.livestatus-end {
  border: solid 1px grey;
  display: inline-block;
  float: left;
  margin: 15px 0 5px 20px;
  background-color: grey;
  color: white;
  padding: 5px 5px 5px 5px;
  font-size: 12px;
  /* text-align: left; */
}
.title {
  clear: both;
  margin: 5px 0 5px 20px;
  text-align: left;
  line-height: 1.3;
}
.time {
  margin: 5px 0 15px 20px;
  text-align: left;
}
.clickable-area {
  cursor: pointer;
}
.cancelrow {
  margin: 0px;
}
.cancelpadding {
  padding: 5px;
}

.image-container {
  position: relative;
  display: inline-block;
  width: 100%;
}

.realtime-label {
  position: absolute;
  top: 0;
  right: 0;
  color: white;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  z-index: 10;
  border-radius: 0 0 0 4px; /* 左下のみ角丸 */
}

/* 配信クラファンの進捗情報 */
.crowdfunding-progress {
  margin: 10px 20px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.progress-amount {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.progress-label {
  font-size: 12px;
  color: #666;
}

.progress-value {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.progress-bar-wrapper {
  width: 100%;
  height: 20px;
  background: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
  position: relative;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #ffdfea 0%, #ff299d 65%, #b000c7 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;
}

.progress-percentage {
  color: white;
  font-weight: bold;
  font-size: 11px;
}

.target-amount {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.target-label {
  font-size: 12px;
  color: #666;
}

.target-value {
  font-size: 14px;
  font-weight: bold;
  color: #333;
}

@media (min-width: 960px) {
  .pc-ma-2 {
    margin: 16px !important;
  }
}
</style>
